/**
 * Firebase web-SDK setup.
 *
 * Unlike the other Barti frontends, this one cannot initialize at module load:
 * the config arrives at runtime from the broker rather than from build-time
 * VITE_* substitution, so initialization is a function the app calls once the
 * config has been fetched.
 */

import { type FirebaseApp, getApps, initializeApp } from 'firebase/app'
import { type Auth, getAuth, GoogleAuthProvider } from 'firebase/auth'

import type { FirebaseWebConfig } from '@/services/definitions'

export const ALLOWED_EMAIL_DOMAINS = ['@barti.com', '@getbarti.com']

/** Whether an email belongs to a Barti staff account the broker will accept. */
export function isBartiAccount(email: string | null | undefined): boolean {
  return ALLOWED_EMAIL_DOMAINS.some((domain) => (email ?? '').endsWith(domain))
}

/** Initialize the Firebase app from runtime config (idempotent). */
export function initFirebaseAuth(config: FirebaseWebConfig): Auth {
  const existing = getApps()[0]
  const app: FirebaseApp =
    existing ??
    initializeApp({
      apiKey: config.api_key,
      authDomain: config.auth_domain,
      projectId: config.project_id,
      appId: config.app_id
    })
  return getAuth(app)
}

/** Google provider for Barti staff sign-in. */
export function createGoogleProvider(): GoogleAuthProvider {
  const provider = new GoogleAuthProvider()
  // No `hd` domain hint: it takes a single domain, but a staff account may be in
  // either of ALLOWED_EMAIL_DOMAINS, so hinting one would pre-select the wrong
  // account for the other. The broker verifies the domain server-side regardless.
  provider.setCustomParameters({ prompt: 'select_account' })
  return provider
}
